import { useCallback, useEffect, useRef } from 'react';

import { FileModel } from 'models';

import { PreloadOptions, filePreloadService } from '../services/FilePreloadService';

interface UseFilePreloadOptions extends PreloadOptions {
  /** Whether preloading is enabled */
  enabled?: boolean;
  /** Debounce delay for file changes in milliseconds */
  debounceMs?: number;
}

/**
 * React hook for file preloading with progressive loading algorithm
 * @param files - Array of files available for preloading
 * @param currentFileId - ID of the currently viewed file
 * @param options - Preloading configuration options
 * @param accessToken - Access token for authenticated file requests
 */
export function useFilePreload(
  files: FileModel[] | null | undefined,
  currentFileId: number | null | undefined,
  options: UseFilePreloadOptions = {},
  accessToken?: string,
) {
  const { enabled = true, debounceMs = 300, ...preloadOptions } = options;

  const debounceRef = useRef<NodeJS.Timeout>();
  const lastFileIdRef = useRef<number | null>(null);

  // Debounced preload function
  const debouncedPreload = useCallback(
    (fileList: FileModel[], fileId: number, config: PreloadOptions) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        filePreloadService.preloadAroundFile(fileList, fileId, config);
      }, debounceMs);
    },
    [debounceMs],
  );

  // Main preload effect
  useEffect(() => {
    if (!enabled || !files || !currentFileId || files.length === 0) {
      return;
    }

    // Skip if file hasn't changed
    if (lastFileIdRef.current === currentFileId) {
      return;
    }

    lastFileIdRef.current = currentFileId;
    debouncedPreload(files, currentFileId, { ...preloadOptions, accessToken });

    // Cleanup function
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [enabled, files, currentFileId, debouncedPreload, preloadOptions, accessToken]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      // Optionally clear all preloads on unmount
      // filePreloadService.clearAll();
    };
  }, []);

  // Manual preload function for specific files
  const preloadFile = useCallback(
    (file: FileModel, priority?: number) => {
      if (enabled) {
        filePreloadService.preloadFile(file, priority);
      }
    },
    [enabled],
  );

  // Clear all preloads
  const clearPreloads = useCallback(() => {
    filePreloadService.clearAll();
  }, []);

  // Get preload statistics
  const getStats = useCallback(() => {
    return filePreloadService.getStats();
  }, []);

  return {
    preloadFile,
    clearPreloads,
    getStats,
  };
}
